import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Card } from 'antd'
import {
  faAt,
  faCity,
  faComment,
  faGlobe,
  faIdBadge,
  faLocationDot,
  faMerge,
  faSave,
  faStethoscope
} from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import IconLabel from '../../../Components/icons/IconLabel'
import { Properties } from '../../../utils/entities'
import InstitutionsList from '../../../Components/shared/Lists/InstitutionsList'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import ConfirmableIconButton from '../../../Components/antd/Buttons/ConfirmableIconButton'
import { getIdOrNull, isObject } from '../../../utils'
import { SchoolContext } from '../../SchoolProvider'
import { normalizeName } from '../../../utils/string'

const { ACRONYM, ADDRESS, CITY, COUNTRY, GLOBAL_MESSAGE, NAME, SECTOR, ZIPCODE, EMAIL } = Properties

const joinParts = (...parts) => parts.filter(p => !!p).join(', ')

const InstitutionValidator = ({ institution, onValidate, onMerge }) => {
  const t = useSelector(selectTranslate)
  const { unmanagedInstitutions } = useContext(SchoolContext)

  const [selectedInstitution, setSelectedInstitution] = useState(null)

  /** A place already known by the school is no candidate to merge with itself. */
  const institutionId = getIdOrNull(institution)

  const duplicate = useMemo(() => {
    const name = normalizeName(institution[NAME])

    return name
      ? unmanagedInstitutions.find(i => i.id !== institutionId && normalizeName(i.name) === name) ?? null
      : null
  }, [institution, institutionId, unmanagedInstitutions])

  /** The place the school already knows is proposed as the merge target : the user only has to confirm it. */
  useEffect(() => {
    setSelectedInstitution(duplicate?.id ?? null)
  }, [duplicate?.id])

  const renderAddress = useCallback(i => {
    const street = joinParts(i[ADDRESS], i[ZIPCODE])
    const city = joinParts(i[CITY], isObject(i[COUNTRY]) ? t(i[COUNTRY].name) : null)

    if (!street && !city) {
      return null
    }

    return (
      <>
        {street && <IconLabel icon={faLocationDot} text={street} fontSize='small' padding='' />}
        {city && <IconLabel icon={faGlobe} text={city} fontSize='small' padding='' />}
      </>
    )
  }, [t])

  return (
    <div className='h-100 w-100 flex-row'>
      <div className='flex-column h-100 w-50 mr-8 jc-between'>
        <h3>{t('institution_validator.title')}</h3>
        <Card size='small' title={institution[NAME]}>
          {/** A place typed in by a student carries neither of the two : he is only asked where he goes. */}
          {institution[ACRONYM] && <IconLabel icon={faIdBadge} text={institution[ACRONYM]} fontSize='small' />}
          {institution[EMAIL] && <IconLabel icon={faAt} text={institution[EMAIL]} fontSize='small' />}
          <IconLabel icon={faCity} text={`${institution[ADDRESS]}, ${institution[ZIPCODE]}`} fontSize='small' />
          <IconLabel icon={faGlobe} text={`${institution[CITY]}, ${institution[COUNTRY]?.name ?? ''}`} fontSize='small' />
          {institution[GLOBAL_MESSAGE] && <IconLabel icon={faComment} text={institution[GLOBAL_MESSAGE]} fontSize='small' />}
          {isObject(institution[SECTOR]) && (<IconLabel icon={faStethoscope} text={institution[SECTOR].name} fontSize='small' />)}
        </Card>
        {/** Creating a place the school already has is still allowed, but never by accident. */}
        <ConfirmableIconButton
          className='mt-12'
          confirmation={isObject(duplicate) ? t('institution_validator.duplicate_confirmation', { institutionName: duplicate.name }) : null}
          disabled={!!selectedInstitution && selectedInstitution !== duplicate?.id}
          icon={faSave}
          text={t('common.validate')}
          type='primary'
          onClick={() => onValidate(institution)}
        />
      </div>
      <div className='flex-column h-100 w-50'>
        <h3>{t('institution_validator.description')}</h3>
        <InstitutionsList
          excluded={institutionId}
          pinned={duplicate?.id}
          renderDetails={renderAddress}
          selected={selectedInstitution}
          onSelectedChange={setSelectedInstitution}
        />
        <IconButton
          className='mt-12'
          disabled={!selectedInstitution}
          icon={faMerge}
          text={t('intitution_validator.merge_button')}
          type='primary'
          onClick={() => onMerge({ id: selectedInstitution })}
        />
      </div>
    </div>
  )
}

export default InstitutionValidator
